import React, {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { Button, Card } from "react-bootstrap";
import { Loading } from "../../ui";
import { AdminTable } from "../admin/AdminTable";
import {
	EditableTextCell,
	SwitchCell,
	SwitchFilter,
	switchFilter,
	TagsCell,
	tagsFilter,
} from "../admin/cells";
import { useFlash } from "../admin/Flash";
import { useSavedRows } from "../admin/useSavedRows";
import { Bundle } from "../bundle/Bundle";
import { BundleService } from "../bundle/BundleService";
import { ProductsNav } from "../ProductsNav";
import { Product } from "./Product";
import { ProductForm } from "./ProductForm";
import { IProductPatch, ProductService } from "./ProductService";

const productService = new ProductService();

function toPatch(changes: Partial<Product>): IProductPatch {
	const { bundles, ...fields } = changes;
	return bundles
		? { ...fields, bundle_ids: bundles.map(bundle => bundle.id as number) }
		: fields;
}

export const ProductPage = (): ReactElement => {
	const [bundles, setBundles] = useState<Bundle[]>();
	const [showCreate, setShowCreate] = useState(false);
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [bulkSaving, setBulkSaving] = useState(false);
	const flash = useFlash();
	const { rows: products, setRows, statuses, save } = useSavedRows<Product>(
		message => flash.show(`Couldn't save: ${message}`, true),
	);

	useEffect(() => {
		new BundleService().findAll().then(setBundles);
		productService.findAll().then(setRows);
	}, []);

	const saveRow = useCallback(
		(id: number, changes: Partial<Product>) =>
			save([id], changes, async () => [
				await productService.patch(id, toPatch(changes)),
			]),
		[save],
	);

	const setActive = async (active: boolean) => {
		const ids = Array.from(selectedIds);
		setBulkSaving(true);
		const saved = await save(ids, { active }, async () => {
			await productService.setActive(ids, active);
		});
		setBulkSaving(false);

		if (saved) {
			setSelectedIds(new Set());
			flash.show(
				`${ids.length} product${ids.length !== 1 ? "s" : ""} ${
					active ? "activated" : "deactivated"
				}`,
			);
		}
	};

	const columns = useMemo(
		() => [
			{ Header: "ID", accessor: "id", width: "6%", disableFilters: true },
			{
				Header: "Name",
				accessor: "name",
				width: "34%",
				Cell: EditableTextCell,
				required: true,
			},
			{
				Header: "Code",
				accessor: "code",
				width: "14%",
				Cell: EditableTextCell,
				required: true,
			},
			{
				Header: "Bundles",
				accessor: "bundles",
				width: "32%",
				Cell: TagsCell,
				options: bundles || [],
				filter: tagsFilter,
				disableSortBy: true,
			},
			{
				Header: "Active",
				accessor: "active",
				width: "12%",
				Cell: SwitchCell,
				Filter: SwitchFilter,
				filter: switchFilter,
				onLabel: "Active",
				offLabel: "Inactive",
			},
		],
		[bundles],
	);

	if (!bundles || !products) {
		return <Loading />;
	}

	const inactiveCount = products.filter(product => !product.active).length;

	return (
		<div className="container mb-3" id="products-admin">
			<div className="products-page-header">
				<div className="products-page-header-left">
					<div className="products-page-icon">
						<i className="fa fa-cube" />
					</div>
					<div>
						<h1>Products</h1>
						<span className="products-page-count">
							{products.length} product
							{products.length !== 1 ? "s" : ""}
							{inactiveCount > 0 &&
								` · ${inactiveCount} inactive`}
						</span>
					</div>
				</div>
				<Button
					variant={showCreate ? "outline-secondary" : "primary"}
					onClick={() => setShowCreate(!showCreate)}
					className="products-create-toggle"
				>
					<i
						className={`fa ${showCreate ? "fa-times" : "fa-plus"}`}
					/>{" "}
					{showCreate ? "Cancel" : "New Product"}
				</Button>
			</div>

			<ProductsNav active="products" />

			{flash.element}

			{showCreate && (
				<Card className="mb-4 products-create-card">
					<Card.Body>
						<ProductForm
							onCreate={product => {
								setRows([product, ...products]);
								setShowCreate(false);
								flash.show(`"${product.name}" created`);
							}}
						/>
					</Card.Body>
				</Card>
			)}

			{selectedIds.size > 0 && (
				<div className="admin-bulk-bar">
					<strong>{selectedIds.size} selected</strong>
					<Button
						size="sm"
						variant="outline-danger"
						disabled={bulkSaving}
						onClick={() => setActive(false)}
					>
						<i className="fa fa-toggle-off" /> Deactivate
					</Button>
					<Button
						size="sm"
						variant="outline-success"
						disabled={bulkSaving}
						onClick={() => setActive(true)}
					>
						<i className="fa fa-toggle-on" /> Activate
					</Button>
					<Button
						size="sm"
						variant="link"
						disabled={bulkSaving}
						onClick={() => setSelectedIds(new Set())}
					>
						Clear selection
					</Button>
					{bulkSaving && (
						<i className="fa fa-spinner fa-spin text-muted" />
					)}
				</div>
			)}

			<div className="editable-data-table products-table-wrapper">
				<AdminTable
					columns={columns}
					data={products}
					statuses={statuses}
					saveRow={saveRow}
					selectedIds={selectedIds}
					onSelectionChange={setSelectedIds}
				/>
			</div>
		</div>
	);
};
